<template>
  <v-container
    fluid
    class="gift-card-page"
  >
    <v-row class="align-center mb-6">
      <v-col
        cols="12"
        md="6"
      >
        <h1 class="page-title">
          Gavekort
        </h1>
        <p class="subtitle-1 text--secondary">
          Administrer gavekorttyper, genererte batcher og aktive kort.
        </p>
      </v-col>
      <v-col
        cols="12"
        md="6"
        class="text-md-right"
      >
        <v-btn
          class="mr-2"
          color="primary"
          @click="openRedeemDialog"
        >
          <v-icon left>
            mdi-cash-refund
          </v-icon>
          Løs inn manuelt
        </v-btn>
        <v-btn
          class="mr-2"
          color="primary"
          :disabled="!types.length"
          @click="openGenerateDialog"
        >
          <v-icon left>
            mdi-cards
          </v-icon>
          Generer batch
        </v-btn>
        <v-btn
          color="primary"
          @click="openTypeDialog"
        >
          <v-icon left>
            mdi-plus
          </v-icon>
          Ny gavekorttype
        </v-btn>
      </v-col>
    </v-row>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <v-card>
      <v-tabs
        v-model="activeTab"
        background-color="transparent"
        color="primary"
      >
        <v-tab>Typer</v-tab>
        <v-tab>Batcher</v-tab>
        <v-tab>Aktive</v-tab>
        <v-tab>Historikk</v-tab>
      </v-tabs>
      <v-divider />

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <gift-card-types-tab
            :types="types"
            :loading="loading"
            @edit="editType"
            @toggle-status="toggleTypeStatus"
          />
        </v-tab-item>

        <v-tab-item>
          <gift-card-batches-tab
            :batches="batches"
            :loading="loading"
            @export="exportBatch"
          />
        </v-tab-item>

        <v-tab-item>
          <gift-card-active-tab
            :active-cards="activeCards"
            :search-result="cardSearchResult"
            :loading="loading"
            @search="handleSearch"
            @redeem="redeemSpecificCard"
          />
        </v-tab-item>

        <v-tab-item>
          <gift-card-history-tab
            :historical-cards="historicalCards"
            :loading="loading"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <gift-card-type-dialog
      v-model="typeDialog"
      :editing-id="editingTypeId"
      :form-data="typeForm"
      @save="saveType"
    />

    <gift-card-batch-dialog
      v-model="batchDialog"
      :types="types"
      :form-data="batchForm"
      @generate="generateBatch"
    />

    <gift-card-redeem-dialog
      v-model="redeemDialog"
      :max-amount="0"
      :initial-code="redeemPrefillCode"
      @confirm="handleRedeem"
    />
  </v-container>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import {
  GiftCardStatus,
  IGiftCardBatchDTO,
  IGiftCardDTO,
  IGiftCardTypeDTO,
  IGenerateGiftCardBatchDTO,
  IRedeemGiftCardRequestDTO,
  IUpsertGiftCardTypeDTO,
} from "@shared/types";
import { giftCardsModule } from "@/store/modules/giftCards/giftCardsModule";
import { snackbarModule } from "@/store/modules/snackbar/snackbarModule";
import { getApiErrorMessage } from "@/Utilities/apiError";
import { userModule } from "@/store/modules/user/userModule";
import GiftCardRedeemDialog from "@/components/giftcards/GiftCardRedeemDialog.vue";
import GiftCardTypesTab from "@/components/giftcards/GiftCardTypesTab.vue";
import GiftCardBatchesTab from "@/components/giftcards/GiftCardBatchesTab.vue";
import GiftCardActiveTab from "@/components/giftcards/GiftCardActiveTab.vue";
import GiftCardHistoryTab from "@/components/giftcards/GiftCardHistoryTab.vue";
import GiftCardTypeDialog from "@/components/giftcards/GiftCardTypeDialog.vue";
import GiftCardBatchDialog from "@/components/giftcards/GiftCardBatchDialog.vue";

interface GiftCardTypeForm {
  id: number | null;
  name: string;
  value: number;
  currency: string;
  durationDays: number | null;
  isActive: boolean;
}

interface GiftCardBatchForm extends IGenerateGiftCardBatchDTO {
  name: string;
  notes: string;
}

@Component({
  name: "gift-card-page",
  components: {
    GiftCardRedeemDialog,
    GiftCardTypesTab,
    GiftCardBatchesTab,
    GiftCardActiveTab,
    GiftCardHistoryTab,
    GiftCardTypeDialog,
    GiftCardBatchDialog,
  },
})
export default class GiftCardPage extends Vue {
  public activeTab = 0;
  public typeDialog = false;
  public batchDialog = false;
  public redeemDialog = false;
  public editingTypeId: number | null = null;
  public cardSearchResult: IGiftCardDTO | null = null;
  public redeemPrefillCode = "";

  public typeForm: GiftCardTypeForm = {
    id: null,
    name: "",
    value: 0,
    currency: "NOK",
    durationDays: null,
    isActive: true,
  };

  public batchForm: GiftCardBatchForm = {
    giftCardTypeId: null as any,
    quantity: 10,
    name: "",
    notes: "",
  };

  get loading(): boolean {
    return giftCardsModule.loading;
  }

  get types(): IGiftCardTypeDTO[] {
    return giftCardsModule.types;
  }

  get batches(): IGiftCardBatchDTO[] {
    return giftCardsModule.batches;
  }

  get cards(): IGiftCardDTO[] {
    return giftCardsModule.cards;
  }

  get activeCards(): IGiftCardDTO[] {
    return this.cards.filter((card) =>
      [GiftCardStatus.Active, GiftCardStatus.Generated, GiftCardStatus.Printed].includes(card.status)
    );
  }

  get historicalCards(): IGiftCardDTO[] {
    return this.cards.filter((card) =>
      [GiftCardStatus.Redeemed, GiftCardStatus.Expired, GiftCardStatus.Voided].includes(card.status)
    );
  }

  async created(): Promise<void> {
    await giftCardsModule.fetchAllData();
    this.typeForm.currency = userModule.user?.currency || "NOK";
  }

  @Watch("activeTab")
  async onTabChange(tab: number) {
    if (tab === 2 || tab === 3) {
      await giftCardsModule.fetchCards();
    }
  }

  public openTypeDialog(): void {
    this.editingTypeId = null;
    this.typeForm = {
      id: null,
      name: "",
      value: 500,
      currency: userModule.user?.currency || "NOK",
      durationDays: null,
      isActive: true,
    };
    this.typeDialog = true;
  }

  public editType(type: IGiftCardTypeDTO): void {
    this.editingTypeId = type.id;
    this.typeForm = {
      id: type.id,
      name: type.name,
      value: type.value,
      currency: type.currency,
      durationDays: type.durationDays ?? null,
      isActive: type.isActive,
    };
    this.typeDialog = true;
  }

  public async saveType(form: GiftCardTypeForm): Promise<void> {
    const payload: IUpsertGiftCardTypeDTO = {
      id: this.editingTypeId ?? undefined,
      name: form.name,
      value: form.value,
      currency: form.currency,
      durationDays: form.durationDays ?? undefined,
      isActive: form.isActive,
    };
    const result = await giftCardsModule.saveType(payload);
    if (result) {
      this.typeDialog = false;
    }
  }

  public async toggleTypeStatus(type: IGiftCardTypeDTO): Promise<void> {
    await giftCardsModule.saveType({
      id: type.id,
      name: type.name,
      value: type.value,
      currency: type.currency,
      durationDays: type.durationDays ?? undefined,
      isActive: !type.isActive,
    });
  }

  public openGenerateDialog(): void {
    if (!this.types.length) {
      snackbarModule.postErrorSnackbarMessage("Opprett en gavekorttype først.");
      return;
    }
    this.batchForm = {
      giftCardTypeId: this.types[0].id,
      quantity: 10,
      name: "",
      notes: "",
    };
    this.batchDialog = true;
  }

  public async generateBatch(form: GiftCardBatchForm): Promise<void> {
    if (!form.giftCardTypeId) return;
    const payload: IGenerateGiftCardBatchDTO = {
      giftCardTypeId: form.giftCardTypeId,
      quantity: form.quantity,
      name: form.name || undefined,
      notes: form.notes || undefined,
    };
    const result = await giftCardsModule.generateBatch(payload);
    if (result) {
      this.batchDialog = false;
    }
  }

  public async exportBatch(batch: IGiftCardBatchDTO): Promise<void> {
    const success = await giftCardsModule.exportBatch({ batchId: batch.id });
    if (success) {
      await giftCardsModule.fetchBatches();
    }
  }

  public openRedeemDialog(): void {
    this.redeemPrefillCode = "";
    this.redeemDialog = true;
  }

  public redeemSpecificCard(card: IGiftCardDTO): void {
    this.cardSearchResult = card;
    this.redeemPrefillCode = card.code;
    this.redeemDialog = true;
  }

  public async handleRedeem(payload: {
    code: string;
    amount: number;
    channel: any;
    notes?: string;
  }): Promise<void> {
    const dto: IRedeemGiftCardRequestDTO = {
      code: payload.code,
      amount: payload.amount,
      channel: payload.channel,
      notes: payload.notes,
    };
    try {
      await giftCardsModule.redeemGiftCard(dto);
    } catch (error) {
      console.error(error);
      snackbarModule.postErrorSnackbarMessage(
        getApiErrorMessage(error, this.$t(this.$ts.giftCardDialogs.redeemFailed).toString())
      );
    }
  }

  public async handleSearch(searchCode: string): Promise<void> {
    if (!searchCode.trim()) {
      this.cardSearchResult = null;
      return;
    }
    const card = await giftCardsModule.lookupByCode(searchCode.trim());
    this.cardSearchResult = card;
  }
}
</script>

<style scoped lang="scss">
.gift-card-page {
  .page-title {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .subtitle-1 {
    margin: 0;
  }
}

.v-data-table {
  .v-btn {
    margin: 0 2px;
  }
}
</style>
